/**
 * Copyright (c) 2023-present,
 * Eunoia Digital, S.L. All rights reserved.
 */

import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from 'next';
import {
  Image,
  SimpleLink,
  TemplateText,
  Text,
} from 'src/presentation/components/atoms';
import { MainLayout } from 'src/presentation/layouts';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { settings } from '@config/settings';
import Carousel from 'react-multi-carousel';
import classNames from 'classnames';
import {
  NoticiaThumbnail,
  ProductCard,
  SolucionesDropdown,
} from 'src/presentation/components/organisms';
import styles from './home-page.module.scss';
import { useTranslation } from 'next-i18next';
import QuotesRightIcon from '@assets/icons/quoteIconRight.svg';
import QuotesLeftIcon from '@assets/icons/quoteIconLeft.svg';

import { useProductos } from 'src/context/useProductos';
import { CountryLocationService } from '@services/country-location-service';
import { localesParserDictionary } from '@utils/locales-parser';
import { EntitiesFetcherService } from '@services/entities-fetcher';
import 'react-multi-carousel/lib/styles.css';
import Link from 'next/link';
import { MainRouter } from '@config/urls';
import { NoticiasService } from '@services/noticias-service';
import { Key, useState } from 'react';
import { Noticia } from '@models/noticia';
import { NextSeo } from 'next-seo';
import { useFamilias } from 'src/context/useFamilias';
import { useProductosGenericos } from 'src/context/useProductosGenericos';
import useFilteredProducts from 'src/context/useFilteredProducts';
import useFilteredAndSortedProducts from 'src/context/useFilteredProducts';
import { filterAndSortProducts } from '@utils/filterAndSortProducts';

const Home: NextPage = (props: any) => {
  const { noticiasData, country, filteredAndSortedProducts } = props;
  const [ultimasNoticias, setUltimasNoticias] = useState<Array<Noticia>>(
    JSON.parse(noticiasData),
  );
  const { t } = useTranslation();

  const TopBanner = () => {
    return (
      <section className={styles.topbanner}>
        <div className={styles.homevideo}>
          <video loop autoPlay muted playsInline preload='auto'>
            <source
              src={'/static/videos/home-web-manvert.mp4'}
              type='video/mp4'
            />
          </video>
        </div>
        <div className={styles.homecontent}>
          <div className={styles.info}>
            <Text as='h1'>{t('HomeView.topbanner.title')}</Text>
            <Text as='h2'>{t('HomeView.topbanner.heading')}</Text>
            <TemplateText template='normal'>
              {t('HomeView.topbanner.description1')}
            </TemplateText>
          </div>
        </div>
        <div className={styles.dropdowncontainer}>
          <SolucionesDropdown search country={country} />
        </div>
      </section>
    );
  };

  const ProductosDestacados = () => {
    const families = useFamilias();

    const productosFinal = filteredAndSortedProducts;

    const responsive = {
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 3,
        slidesToSlide: 3,
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 2,
        slidesToSlide: 2,
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1,
      },
    };
    return (
      <section className={styles.productosDestacados}>
        {productosFinal && (
          <Carousel
            responsive={responsive}
            ssr
            infinite={false}
            containerClass='first-carousel-container container'
          >
            {productosFinal
              .slice(0, 5)
              .map(
                (item: {
                  id: number;
                  slug: string | undefined;
                  nombre: string | undefined;
                  ecologico: boolean | undefined;
                  internacional: boolean | undefined;
                  familia: number;
                  imagen_catalogo: string | undefined;
                  descripcion2: string;
                }) => {
                  return (
                    <div key={item.id} className={styles.wrapper}>
                      <ProductCard
                        id={item.id}
                        slug={item.slug}
                        name={item.nombre}
                        eco={item.ecologico}
                        internacional={item.internacional}
                        category={
                          families.find((x) => x.id === item.familia)?.nombre
                        }
                        categoryColor={
                          families.find((x) => x.id === item.familia)?.color
                        }
                        image={item.imagen_catalogo}
                        descripcion={[item.descripcion2]}
                        country={country}
                        animated={false}
                        margin={5}
                      />
                    </div>
                  );
                },
              )}
          </Carousel>
        )}
      </section>
    );
  };

  const NuestrosClientes = () => {
    const opinions = [
      {
        key: 'opinion1',
      },
      {
        key: 'opinion4',
      },
      {
        key: 'opinion2',
      },
      {
        key: 'opinion3',
      },
      {
        key: 'opinion5',
      },
    ];
    const responsive = {
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 1,
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 1,
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
      },
    };
    return (
      <section className={styles.nuestrosClientes}>
        <div className={styles.opinionsContainer}>
          <Carousel
            responsive={responsive}
            ssr
            infinite={false}
            containerClass='first-carousel-container container'
          >
            {opinions.map((item, index) => (
              <div key={index} className={styles.opinion}>
                <div className={styles.video}>
                  <iframe
                    loading='lazy'
                    title='Test'
                    src={
                      t(
                        'HomeView.nuestrosClientesSection.' +
                          item.key +
                          '.link',
                      ) || ''
                    }
                    frameBorder='0'
                    allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                  />
                </div>
                <div className={styles.datacontainer}>
                  <div className={styles.descriptionContainer}>
                    <QuotesLeftIcon />
                    <div className={styles.textcontainer}>
                      <TemplateText template='normal'>
                        {t(
                          'HomeView.nuestrosClientesSection.' +
                            item.key +
                            '.text',
                        )}
                      </TemplateText>
                      <TemplateText template='strong'>
                        {t(
                          'HomeView.nuestrosClientesSection.' +
                            item.key +
                            '.quote1',
                        )}
                      </TemplateText>
                      <br />
                      <Text>
                        {t(
                          'HomeView.nuestrosClientesSection.' +
                            item.key +
                            '.quote2',
                        )}
                      </Text>
                    </div>
                    <QuotesRightIcon size={18} />
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      </section>
    );
  };

  const ComunidadManvert = () => {
    return (
      <section className={styles.comunidadManvert}>
        <Text as='h2'>{t('HomeView.comunidadManvertSection.heading')}</Text>
        <div className={styles.topRow}>
          <Link
            legacyBehavior
            passHref
            href={MainRouter['hazte-distribuidor'].generatePath()}
          >
            <div className={styles.item}>
              <div className={styles.left}>
                <Image
                  loading='lazy'
                  src={
                    '/static/images/home-comunidad-manvert-bioestimulantes.webp'
                  }
                  alt='home comunidad manvert bioestimulantes'
                />
              </div>
              <div className={styles.right}>
                <div className={styles.frame}></div>
                <Text as='h3'>
                  {t('HomeView.comunidadManvertSection.title1')}
                </Text>
                <TemplateText template='normal'>
                  {t('HomeView.comunidadManvertSection.p1')}
                </TemplateText>
              </div>
            </div>
          </Link>
          <Link
            legacyBehavior
            passHref
            href={MainRouter.contactanos.generatePath()}
          >
            <div className={styles.item}>
              <div className={styles.left}>
                <Image
                  loading='lazy'
                  src={
                    '/static/images/home-comunidad-productos-manvert-bioestimulantes.webp'
                  }
                  alt='home comunidad productos manvert bioestimulantes'
                />
              </div>
              <div className={styles.right}>
                <div className={styles.frame}></div>
                <Text as='h3'>
                  {t('HomeView.comunidadManvertSection.title2')}
                </Text>
                <TemplateText template='normal'>
                  {t('HomeView.comunidadManvertSection.p2')}
                </TemplateText>
              </div>
            </div>
          </Link>
        </div>
      </section>
    );
  };

  const BiovertEnMundo = () => {
    return (
      <section className={styles.biovertMundoSection}>
        <Text as='h2'>{t('HomeView.biovertMundoSection.head')}</Text>
        <a
          href={MainRouter['biovert-mundo'].generatePath()}
          style={{ justifyContent: 'flex-start' }}
          className={classNames(styles.item, styles.inverse)}
        >
          <Image
            loading='lazy'
            className={styles.inverse}
            src={'/static/images/map-manvert.webp'}
            alt='home manvert croptology bioestimulantes'
          />
          <div className={styles.card} style={{ right: '5%' }}>
            <Text as='h3'>{t('HomeView.biovertMundoSection.title')}</Text>
            <TemplateText template='small'>
              {t('HomeView.biovertMundoSection.p1')}
            </TemplateText>
            <SimpleLink
              simbolyc
              label={t('HomeView.biovertMundoSection.link1')}
              link={MainRouter['biovert-mundo'].generatePath()}
            />
          </div>
        </a>
      </section>
    );
  };

  const Croptology = () => {
    return (
      <section className={styles.croptologySection}>
        <Text as='h2'>Croptology</Text>
        <a
          href={MainRouter.croptology.generatePath()}
          style={{ justifyContent: 'flex-start' }}
          className={styles.item}
        >
          <div className={styles.card} style={{ right: '5%' }}>
            <Text as='h3'>{t('HomeView.croptologySection.heading')}</Text>
            <TemplateText template='small'>
              {t('HomeView.croptologySection.description1')}
            </TemplateText>
            <TemplateText template='small'>
              {t('HomeView.croptologySection.description2')}
            </TemplateText>
            <SimpleLink
              simbolyc
              label={t('HomeView.croptologySection.link1')}
              link={MainRouter.croptology.generatePath()}
            />
          </div>
          <Image
            loading='lazy'
            className={styles.normal}
            src={'/static/images/home-manvert-croptology-bioestimulantes.webp'}
            alt='home manvert croptology bioestimulantes'
          />
        </a>
      </section>
    );
  };

  const UltimasNoticias = () => {
    return (
      <section className={styles.noticiasSection}>
        <Text as='h2'>{t('HomeView.noticiasSection.title')}</Text>
        <div className={styles.noticiasWrapper}>
          {ultimasNoticias.map((noticia) => {
            return (
              <NoticiaThumbnail
                key={noticia.slug}
                title={noticia.titulo}
                image={noticia.imagen_cabecera.url}
                date={noticia.fecha}
                slug={noticia.slug}
              />
            );
          })}
        </div>
        <Link legacyBehavior href={MainRouter.medios.generatePath()}>
          <a className={styles.todosLink}>
            {t('HomeView.noticiasSection.todas')}
          </a>
        </Link>
      </section>
    );
  };

  return (
    <>
      <NextSeo
        title={t('SEOMeta.HomeView.title') || ''}
        description={t('SEOMeta.HomeView.description') || ''}
      />
      <MainLayout>
        <div className={styles.root}>
          <TopBanner />
          <ProductosDestacados />
          <NuestrosClientes />
          <ComunidadManvert />
          <BiovertEnMundo />
          <Croptology />
          <UltimasNoticias />
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;

  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const noticias = await new NoticiasService().getUltimasNoticias(
    opts.locale,
    3,
  );
  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productos: true,
    productosDestacados: true,
    productosGenericos: true,
  });
  // Obtener productos y productos genéricos
  const productosData = entities.productosData || [];
  const productosGenericosData = entities.productosGenericosData || [];

  const filteredAndSortedProducts = filterAndSortProducts(
    productosData,
    productosGenericosData,
  );

  return {
    props: {
      country: selectedCountry.countrycode,
      noticiasData: JSON.stringify(noticias),
      productosData: JSON.stringify(
        entities.productosData ? entities.productosData.slice(0, 5) : [],
      ),
      filteredAndSortedProducts: JSON.parse(
        JSON.stringify(filteredAndSortedProducts),
      ),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        'common',
      ])),
    },
  };
}

export default Home;
